import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Link,
  Grid,
  useTheme,
  useMediaQuery,
  Fade,
  Avatar
} from '@mui/material';
import { ArrowBack, EmailOutlined, CheckCircleOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import LanguageSwitcher from '../components/LanguageSwitcher';

const ForgotPasswordPage = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      await axios.post(
        `${API_URL}/auth/forgot-password`,
        { email },
        {
          headers: {
            'Accept-Language': i18n.language
          }
        }
      );

      setSuccess(true);
    } catch (err) {
      // Backend returns error in 'error' field, not 'message'
      const errorMessage = err.response?.data?.error || err.response?.data?.message;
      const errorKey = err.response?.data?.errorKey;

      // If we have an errorKey, try to translate it
      if (errorKey) {
        // Backend sends keys like "passwordReset:emailSent"
        // Frontend needs "errors:passwordReset.emailSent" (with namespace)
        const frontendKey = 'errors:' + errorKey.replace(':', '.');
        const translatedError = t(frontendKey);

        // Only use translation if it's not the same as the key (meaning translation exists)
        if (translatedError !== frontendKey) {
          setError(translatedError);
        } else {
          setError(errorMessage || t('auth:forgotPassword.errors.sendFailed'));
        }
      } else {
        setError(errorMessage || t('auth:forgotPassword.errors.sendFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <Box sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: theme => theme.palette.background.default,
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Background gradient effect */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${theme.palette.primary[900]} 0%, ${theme.palette.primary[800]} 35%, ${theme.palette.primary[700]} 100%)`,
            opacity: 0.95,
            zIndex: 0
          }}
        />

        {/* Language Switcher */}
        <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 3 }}>
          <LanguageSwitcher variant="full" />
        </Box>

        <Container maxWidth="sm" sx={{ zIndex: 2, my: 4, display: 'flex', alignItems: 'center' }}>
          <Fade in timeout={800}>
            <Paper
              elevation={24}
              sx={{
                width: '100%',
                borderRadius: 3,
                p: { xs: 3, sm: 5 },
                backdropFilter: 'blur(10px)',
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                textAlign: 'center'
              }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <CheckCircleOutline
                  sx={{
                    fontSize: 80,
                    color: theme.palette.success.main,
                    mb: 2
                  }}
                />
              </motion.div>

              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 'bold',
                  mb: 2,
                  color: theme.palette.primary.main
                }}
              >
                {t('auth:forgotPassword.success.title')}
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {t('auth:forgotPassword.success.message')}
              </Typography>

              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                {t('auth:forgotPassword.success.checkEmail')}
              </Alert>

              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                fullWidth
                startIcon={<ArrowBack />}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.accent.mint} 100%)`
                }}
              >
                {t('auth:forgotPassword.buttons.backToLogin')}
              </Button>
            </Paper>
          </Fade>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      minHeight: '100vh',
      bgcolor: theme => theme.palette.background.default,
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Background gradient effect */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${theme.palette.primary[900]} 0%, ${theme.palette.primary[800]} 35%, ${theme.palette.primary[700]} 100%)`,
          opacity: 0.95,
          zIndex: 0
        }}
      />

      {/* Decorative circles */}
      <Fade in timeout={1000}>
        <Box
          sx={{
            position: 'absolute',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: theme.palette.accent.mint,
            filter: 'blur(80px)',
            opacity: 0.2,
            top: '-50px',
            right: '-50px',
            zIndex: 1
          }}
        />
      </Fade>

      {/* Language Switcher */}
      <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 3 }}>
        <LanguageSwitcher variant="full" />
      </Box>

      <Container maxWidth="sm" sx={{ zIndex: 2, my: 4, display: 'flex', alignItems: 'center' }}>
        <Fade in timeout={800}>
          <Paper
            elevation={24}
            sx={{
              width: '100%',
              borderRadius: 3,
              p: { xs: 3, sm: 5 },
              backdropFilter: 'blur(10px)',
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Mobile logo */}
            {isMobile && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                <Avatar
                  src="/logo192.png"
                  alt="MetuMotion Logo"
                  sx={{
                    width: 80,
                    height: 80,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}
                />
              </Box>
            )}

            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <EmailOutlined
                sx={{
                  fontSize: 60,
                  color: theme.palette.primary.main,
                  mb: 2
                }}
              />
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 'bold',
                  mb: 1,
                  color: theme.palette.primary.main
                }}
              >
                {t('auth:forgotPassword.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('auth:forgotPassword.subtitle')}
              </Typography>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2
                }}
              >
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    label={t('auth:forgotPassword.form.email.label')}
                    name="email"
                    autoComplete="email"
                    autoFocus
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 2 }
                    }}
                    helperText={t('auth:forgotPassword.form.email.helperText')}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 4 }}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.accent.mint} 100%)`,
                    boxShadow: '0 4px 10px rgba(17, 138, 178, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 6px 15px rgba(17, 138, 178, 0.4)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  {isSubmitting
                    ? t('auth:forgotPassword.buttons.sending')
                    : t('auth:forgotPassword.buttons.sendResetLink')}
                </Button>
              </Box>

              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('auth:forgotPassword.links.rememberPassword')}{' '}
                  <Link
                    component={RouterLink}
                    to="/login"
                    underline="hover"
                    sx={{
                      fontWeight: 'medium',
                      color: theme.palette.primary.main,
                      transition: 'color 0.2s',
                      '&:hover': {
                        color: theme.palette.accent.mint
                      }
                    }}
                  >
                    {t('auth:forgotPassword.links.backToLogin')}
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default ForgotPasswordPage;
